import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, ExternalLink } from 'lucide-react'
import { IntegrationType } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'

// ---------------------------------------------------------------------------
// Credential field definitions per provider
// ---------------------------------------------------------------------------

interface CredentialField {
  id: string
  label: string
  placeholder: string
  type?: 'text' | 'password'
  valueType?: 'string' | 'csv'
  helpText?: string
  required?: boolean
}

interface ApiKeyProviderConfig {
  id: IntegrationType
  name: string
  description: string
  authMode?: 'api_key' | 'oauth_client_credentials' | 'oauth_refresh_token' | 'service_account' | 'bearer_token'
  fields: CredentialField[]
  docsUrl?: string
  featureId: string
  featureName: string
  featureDescription: string
}

// ---------------------------------------------------------------------------
// API Key Config Modal
// ---------------------------------------------------------------------------

function ApiKeyConfigModal({
  config,
  open,
  onOpenChange,
  onComplete,
  integration,
}: ConfigModalProps & { config: ApiKeyProviderConfig }) {
  const [isSaving, setIsSaving] = useState(false)
  const [values, setValues] = useState<Record<string, string>>({})

  const create = useMutation(trpc.integrations.create.mutationOptions())
  const update = useMutation(trpc.integrations.updateIntegration.mutationOptions())
  const isEdit = !!integration

  const handleSave = async () => {
    // Validate all fields are filled
    for (const field of config.fields) {
      if (field.required !== false && !values[field.id]?.trim()) {
        toast.error(`${field.label} is required`)
        return
      }
    }

    setIsSaving(true)
    try {
      const credentialConfig = {
        ...Object.fromEntries(
          config.fields
            .map((field) => {
              const value = values[field.id]?.trim()
              if (field.valueType !== 'csv') return [field.id, value] as const

              const entries = value
                ?.split(',')
                .map(entry => entry.trim())
                .filter(Boolean)
              return [field.id, entries?.length ? [...new Set(entries)] : undefined] as const
            })
            .filter(([, value]) => value)
        ),
        authMode: config.authMode ?? 'api_key',
        lastConfiguredAt: new Date().toISOString(),
      }

      if (isEdit) {
        // Update existing integration
        await update.mutateAsync({
          id: integration.id,
          config: credentialConfig,
          metadata: {
            ...integration.metadata,
            lastConfiguredAt: new Date().toISOString(),
          },
        })
        toast.success(`${config.name} updated`)
      } else {
        // Create new integration
        await create.mutateAsync({
          type: config.id,
          name: config.name,
          config: credentialConfig,
          metadata: {
            lastConfiguredAt: new Date().toISOString(),
          },
        })
        toast.success(`${config.name} connected`)
      }

      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onOpenChange(false)
      onComplete()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : `Failed to save ${config.name} integration`
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type={config.id} />
            {isEdit ? `Manage ${config.name}` : `Connect ${config.name}`}
          </DialogTitle>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 py-2 px-6" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          {config.fields.map(field => (
            <div key={field.id} className="space-y-2">
              <Label htmlFor={`field-${field.id}`}>{field.label}</Label>
              <Input
                id={`field-${field.id}`}
                name={`${config.id}-${field.id}`}
                type={field.type || 'text'}
                autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={field.placeholder}
                value={values[field.id] || ''}
                onChange={e => setValues(prev => ({ ...prev, [field.id]: e.target.value }))}
              />
              {field.helpText && (
                <p className="text-[11px] text-text-subtle">{field.helpText}</p>
              )}
            </div>
          ))}

          {config.docsUrl && (
            <a
              href={config.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-cobalt-strong hover:underline"
            >
              How to get your API credentials
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </form>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? 'Update' : 'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Provider factory
// ---------------------------------------------------------------------------

function createApiKeyProvider(config: ApiKeyProviderConfig): IntegrationProvider {
  return {
    id: config.id,
    name: config.name,
    description: config.description,
    Icon: () => <IntegrationIcon type={config.id} />,
    ConfigModal: (props) => <ApiKeyConfigModal {...props} config={config} />,
    handleConnect: async ({ onSuccess }) => {
      // Open the config modal directly (same pattern as cloud providers)
      onSuccess?.({ provider: config.id })
      return { success: true }
    },
    getFeatures: () => [
      {
        id: config.featureId,
        name: config.featureName,
        description: config.featureDescription,
        enabled: true,
      },
    ],
    syncData: async () => {},
  }
}

// ---------------------------------------------------------------------------
// Identity Providers (RFC-148)
// ---------------------------------------------------------------------------

export const OktaProvider = createApiKeyProvider({
  id: 'Okta',
  name: 'Okta',
  description: 'Scan MFA policies, password strength, session controls, and user lifecycle management',
  docsUrl: 'https://developer.okta.com/docs/guides/create-an-api-token/main/',
  featureId: 'idp_scan',
  featureName: 'Identity scan',
  featureDescription: 'Run deterministic identity provider compliance checks',
  fields: [
    { id: 'domain', label: 'Okta domain', placeholder: 'your-org.okta.com', helpText: 'Your Okta organization domain (without https://)' },
    { id: 'apiToken', label: 'API token', placeholder: 'Okta API token', type: 'password' },
    { id: 'externalUserTypeIds', label: 'External identity user type IDs (optional)', placeholder: 'otu... , otu...', helpText: 'Comma-separated Okta User Type IDs explicitly used for guests, partners, or contractors.', required: false },
    { id: 'externalGroupIds', label: 'External identity group IDs (optional)', placeholder: '00g... , 00g...', helpText: 'Comma-separated Okta Group IDs explicitly used for external identities.', required: false },
  ],
})

// Why service-account + domain-wide delegation, and NOT a "Connect with Google"
// (3-legged user OAuth) flow:
//
// The Admin SDK scopes are restricted, and the Cloud Identity Policy API
// additionally requires its exact read-only scope in domain-wide delegation.
// A published app that lets external customers consent to restricted scopes must pass Google OAuth
// verification PLUS an annual third-party CASA security assessment (verified domain,
// privacy policy, demo; multi-week, paid). We have NOT completed CASA, so a user-OAuth
// flow would be capped at <=100 test users and everyone else hits the "Google hasn't
// verified this app" wall — effectively unusable for real customers.
//
// Service-account + domain-wide delegation sidesteps verification entirely: it uses the
// CUSTOMER'S own service account and THEIR own delegation grant, so Google requires no
// app verification on our side. Worse onboarding UX, but it actually works today.
//
// The backend adapter (server/.../adapters/google-workspace.adapter.ts) already accepts
// a raw OAuth `accessToken`, so switching to "Connect with Google" is mostly a UI/flow
// change once CASA is done — it is gated on that assessment, not on code.
export const GoogleWorkspaceProvider = createApiKeyProvider({
  id: 'GoogleWorkspace',
  name: 'Google Workspace',
  description: 'Scan identity posture, Workspace security policies, and admin audit access',
  authMode: 'service_account',
  docsUrl: 'https://support.google.com/a/answer/7281227',
  featureId: 'idp_scan',
  featureName: 'Identity scan',
  featureDescription: 'Run deterministic identity provider compliance checks',
  fields: [
    { id: 'domain', label: 'Workspace domain', placeholder: 'your-company.com' },
    { id: 'serviceAccountKey', label: 'Service account key (JSON)', placeholder: 'Paste service account JSON key', type: 'password', helpText: 'Enable the Cloud Identity API. Domain-wide delegation requires https://www.googleapis.com/auth/admin.directory.user.readonly and https://www.googleapis.com/auth/admin.directory.rolemanagement.readonly. Add https://www.googleapis.com/auth/cloud-identity.policies.readonly for policy checks and https://www.googleapis.com/auth/admin.reports.audit.readonly for audit logs.' },
    { id: 'adminEmail', label: 'Admin email', placeholder: 'admin@your-company.com', helpText: 'A super admin email for delegated access' },
  ],
})

export const Auth0Provider = createApiKeyProvider({
  id: 'Auth0',
  name: 'Auth0',
  description: 'Scan MFA enforcement, brute force protection, password policies, and session settings',
  authMode: 'oauth_client_credentials',
  docsUrl: 'https://auth0.com/docs/secure/tokens/access-tokens/management-api-access-tokens',
  featureId: 'idp_scan',
  featureName: 'Identity scan',
  featureDescription: 'Run deterministic identity provider compliance checks',
  fields: [
    { id: 'domain', label: 'Auth0 domain', placeholder: 'your-tenant.auth0.com' },
    { id: 'clientId', label: 'Client ID', placeholder: 'Management API client ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'Management API client secret', type: 'password' },
  ],
})

export const OneLoginProvider = createApiKeyProvider({
  id: 'OneLogin',
  name: 'OneLogin',
  description: 'Scan MFA policies, password requirements, session timeouts, and directory sync status',
  authMode: 'oauth_client_credentials',
  docsUrl: 'https://developers.onelogin.com/api-docs/1/getting-started/working-with-api-credentials',
  featureId: 'idp_scan',
  featureName: 'Identity scan',
  featureDescription: 'Run deterministic identity provider compliance checks',
  fields: [
    { id: 'subdomain', label: 'OneLogin subdomain', placeholder: 'your-company', helpText: 'The subdomain from your-company.onelogin.com' },
    { id: 'clientId', label: 'Client ID', placeholder: 'API credential client ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'API credential client secret', type: 'password' },
  ],
})

export const JumpCloudProvider = createApiKeyProvider({
  id: 'JumpCloud',
  name: 'JumpCloud',
  description: 'Scan MFA enforcement, conditional access, device trust, and system insights configuration',
  docsUrl: 'https://docs.jumpcloud.com/api/1.0/index.html#section/Authentication-and-Authorization',
  featureId: 'idp_scan',
  featureName: 'Identity scan',
  featureDescription: 'Run deterministic identity provider compliance checks',
  fields: [
    { id: 'apiKey', label: 'API key', placeholder: 'JumpCloud API key', type: 'password', helpText: 'Found in JumpCloud Admin Console under your user icon > API Settings' },
    { id: 'orgId', label: 'Organization ID', placeholder: 'JumpCloud organization ID', helpText: 'Required for multi-tenant JumpCloud accounts' },
  ],
})

// ---------------------------------------------------------------------------
// Security & Monitoring (RFC-148)
// ---------------------------------------------------------------------------

export const DatadogProvider = createApiKeyProvider({
  id: 'Datadog',
  name: 'Datadog',
  description: 'Scan monitor coverage, alert routing, log retention, audit trail, and RBAC configuration',
  docsUrl: 'https://docs.datadoghq.com/account_management/api-app-keys/',
  featureId: 'security_scan',
  featureName: 'Security scan',
  featureDescription: 'Run deterministic security tool compliance checks',
  fields: [
    { id: 'apiKey', label: 'API key', placeholder: 'Datadog API key', type: 'password' },
    { id: 'appKey', label: 'Application key', placeholder: 'Datadog application key', type: 'password' },
    { id: 'site', label: 'Datadog site', placeholder: 'datadoghq.com', helpText: 'e.g. datadoghq.com, datadoghq.eu, us3.datadoghq.com' },
  ],
})

export const AxiomProvider = createApiKeyProvider({
  id: 'Axiom',
  name: 'Axiom',
  description: 'Scan dataset retention, monitor coverage, alert routing, and monitor status',
  authMode: 'bearer_token',
  docsUrl: 'https://axiom.co/docs/reference/tokens',
  featureId: 'security_scan',
  featureName: 'Security scan',
  featureDescription: 'Run deterministic observability compliance checks',
  fields: [
    { id: 'token', label: 'Advanced API token', placeholder: 'Axiom advanced API token', type: 'password' },
    { id: 'datasetIds', label: 'Dataset IDs', placeholder: 'dataset-one, dataset-two', valueType: 'csv', helpText: 'Comma-separated datasets included in compliance checks' },
  ],
})

export const CrowdStrikeProvider = createApiKeyProvider({
  id: 'CrowdStrike',
  name: 'CrowdStrike',
  description: 'Scan Falcon sensor coverage, prevention policies, and detection response configuration',
  authMode: 'oauth_client_credentials',
  docsUrl: 'https://falcon.crowdstrike.com/documentation/46/crowdstrike-oauth2-based-apis',
  featureId: 'security_scan',
  featureName: 'Security scan',
  featureDescription: 'Run deterministic security tool compliance checks',
  fields: [
    { id: 'clientId', label: 'Client ID', placeholder: 'Falcon API client ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'Falcon API client secret', type: 'password' },
    { id: 'baseUrl', label: 'Base URL', placeholder: 'https://api.crowdstrike.com', helpText: 'US-1: api.crowdstrike.com, US-2: api.us-2.crowdstrike.com, EU-1: api.eu-1.crowdstrike.com' },
  ],
})

export const SnykProvider = createApiKeyProvider({
  id: 'Snyk',
  name: 'Snyk',
  description: 'Scan project coverage, vulnerability scanning policies, and fix rate tracking',
  docsUrl: 'https://docs.snyk.io/snyk-api/authentication-for-api',
  featureId: 'security_scan',
  featureName: 'Security scan',
  featureDescription: 'Run deterministic security tool compliance checks',
  fields: [
    { id: 'apiToken', label: 'API token', placeholder: 'Snyk API token', type: 'password', helpText: 'Found in Snyk Account Settings > API Token' },
    { id: 'orgId', label: 'Organization ID', placeholder: 'Snyk organization ID', helpText: 'Found in Snyk Organization Settings > General' },
  ],
})

/**
 * Supabase is dual-mode: OAuth first, personal access token as the fallback.
 *
 * OAuth is preferred because it is the ONLY path with renewal — a Supabase PAT
 * caps at one year, cannot be rotated, and takes every check down with it when
 * it lapses. The refresh token from OAuth is kept live by
 * `oauth-token-refresh.service.ts`.
 *
 * The PAT form is still reachable and still works, for accounts where the
 * OAuth app is unavailable (self-hosted, or an org that blocks third-party
 * apps) and for anyone who would rather paste a token than grant consent.
 *
 * `handleConnect` asks the SERVER whether OAuth is configured rather than
 * assuming it: opening a consent popup against a server with no client_id
 * dead-ends on an opaque error, whereas falling through to the token form
 * always works.
 */
const supabaseApiKeyProvider = createApiKeyProvider({
  id: 'Supabase',
  name: 'Supabase',
  description: 'Scan projects for row-level security gaps, SSL enforcement, PITR backups, database network restrictions, Postgres currency, and organization member MFA.',
  // The access-tokens page, NOT the API-keys guide — this form wants a
  // personal access token (sbp_), and the API-keys docs describe the
  // project-level anon/service_role keys, which are a different credential.
  docsUrl: 'https://supabase.com/dashboard/account/tokens',
  featureId: 'cloud_scan',
  featureName: 'Database scan',
  featureDescription: 'Run deterministic RLS, encryption, backup, network, and access compliance checks',
  fields: [
    // Token only. Scoping is NOT collected here: making someone hunt for org
    // slugs and 20-character project ids before they can connect is the wrong
    // moment to ask, and the answer is already available from the API the
    // moment the token is valid. Per-project scoping is a post-connect
    // selection over enumerated projects (the Azure pattern), carried by
    // IntegrationConnection rows and the existing `target_connections`
    // multi-select. The adapter still honours `organizationSlug` / `projectRefs`
    // credentials — that is the primitive those connection rows will set.
    { id: 'accessToken', label: 'Access token', placeholder: 'sbp_...', type: 'password', helpText: 'Create a personal access token at https://supabase.com/dashboard/account/tokens. Supabase has no read-only or project-scoped token. It carries the full authority of the account that created it, so use a dedicated service account rather than a founder login where you can. Note this token cannot be renewed: connecting with Supabase above keeps itself alive instead.' },
  ],
})

/**
 * True when this integration was connected via OAuth rather than a pasted
 * token — the two need different manage surfaces, because an OAuth connection
 * has no credential for the user to supply or re-enter.
 *
 * Reads the TOP-LEVEL `authMode`, not `config.authMode`. `toSafeIntegrationResponse`
 * withholds `config` from the client (it holds the access and refresh tokens)
 * and lifts this one non-secret field out of it precisely so manage modals can
 * branch. Reading `config.authMode` here silently yields `undefined` and falls
 * through to the credentials form.
 */
function isSupabaseOAuthConnection(integration?: { authMode?: string | null } | null): boolean {
  return integration?.authMode === 'oauth'
}

/** Shared by the connect button and the manage dialog's Reconnect action. */
async function startSupabaseOAuth({
  workspaceId,
  redirectUrl,
  onSuccess,
  onFinish,
}: {
  workspaceId: string
  redirectUrl?: string
  onSuccess?: (result: unknown) => void
  onFinish?: (status: 'success' | 'error' | 'closed') => void
}): Promise<{ success: boolean; error?: string }> {
  const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin
  const callbackUrl = redirectUrl ?? `${baseWebUrl}/auth-callback/Supabase`

  try {
    const init = await trpcClient.integrations.initSupabaseAuth.mutate({
      workspaceId,
      redirectUrl: callbackUrl,
    })
    if (!init.authUrl) throw new Error('Supabase did not return an authorization URL')

    openOAuthConnectPopup({
      authUrl: init.authUrl,
      windowName: 'supabase-oauth',
      provider: 'Supabase',
      onSuccess,
      onError: (message) => toast.error(`Failed to connect Supabase: ${message}`),
      onPopupBlocked: () =>
        toast.error('Popup blocked. Allow popups for this site and try again.'),
      onFinish,
    })
    return { success: true }
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to start Supabase authorization'
    toast.error(message)
    onFinish?.('error')
    return { success: false, error: message }
  }
}

/**
 * Manage view for an OAuth-connected Supabase.
 *
 * An OAuth connection has no user-supplied credential, so showing the access
 * token form here asks for something that does not exist and cannot be
 * supplied — the Update button then blocks on a required field the user has no
 * way to fill. This surface reports state instead, and offers the two actions
 * that ARE meaningful: re-run consent, or deliberately switch to a token.
 */
function SupabaseManageDialog({
  open,
  onOpenChange,
  onComplete,
  workspaceId,
  integration,
  onUseAccessToken,
}: ConfigModalProps & { onUseAccessToken: () => void }) {
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  const metadata = (integration?.metadata ?? {}) as Record<string, unknown>
  // Token expiry lives in the redacted `config`, so it never reaches the
  // client. Last connect time is the honest substitute — inventing an expiry
  // we cannot see would be worse than omitting it, and the refresher makes the
  // exact value irrelevant anyway.
  const lastConnectedAt =
    typeof metadata.lastConnectedAt === 'string'
      ? new Date(metadata.lastConnectedAt)
      : null

  // Two modes, one dialog. Straight after connect the user has never chosen a
  // scope, so this is step 2 of the connect flow and the only sensible primary
  // action is "confirm what I scan". Afterwards it is a management surface,
  // where the primary action is simply leaving — scope toggles already save
  // themselves, and a prominent Reconnect there reads as "click me to apply",
  // which is exactly the confusion it caused.
  const isFirstRunScopeStep = typeof metadata.scopeConfirmedAt !== 'string'

  const handleReconnect = async () => {
    setIsReconnecting(true)
    try {
      await startSupabaseOAuth({
        workspaceId,
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
          })
          toast.success('Supabase reconnected')
          onOpenChange(false)
          onComplete()
        },
        onFinish: () => setIsReconnecting(false),
      })
    } catch {
      setIsReconnecting(false)
    }
  }

  const handleConfirmScope = async () => {
    setIsConfirming(true)
    try {
      await trpcClient.integrations.confirmSupabaseScope.mutate()
      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onOpenChange(false)
      onComplete()
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save project scope',
      )
    } finally {
      setIsConfirming(false)
    }
  }

  const busy = isReconnecting || isConfirming

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <IntegrationIcon type='Supabase' />
            {isFirstRunScopeStep ? 'Choose what to scan' : 'Manage Supabase'}
          </DialogTitle>
          <DialogDescription>
            {isFirstRunScopeStep
              ? 'Supabase is connected. Pick the projects that are in audit scope. You can change this any time.'
              : 'Connected with Supabase OAuth. Access renews automatically, so there is no token to maintain.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 px-6 py-2'>
          <SupabaseProjectScopeList />

          {!isFirstRunScopeStep && (
            <div className='space-y-2 rounded-sm border border-border p-4'>
              <div className='flex items-center justify-between'>
                <span className='text-[12px] font-medium uppercase tracking-wider text-text-subtle'>
                  Connection
                </span>
                <span className='text-[13px] text-status-green'>OAuth</span>
              </div>
              <p className='text-[12px] text-text-subtle'>
                {lastConnectedAt
                  ? `Connected ${lastConnectedAt.toLocaleString()}. `
                  : ''}
                Access is refreshed automatically, so there is no token to
                rotate or re-enter.
              </p>
              <div className='flex items-center gap-3 pt-1'>
                <button
                  type='button'
                  onClick={handleReconnect}
                  disabled={busy}
                  className='text-[12px] text-cobalt-strong hover:underline disabled:opacity-50'
                >
                  {isReconnecting ? 'Reconnecting…' : 'Reconnect'}
                </button>
                <button
                  type='button'
                  onClick={onUseAccessToken}
                  disabled={busy}
                  className='text-[12px] text-cobalt-strong hover:underline disabled:opacity-50'
                >
                  Use a personal access token instead
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {isFirstRunScopeStep ? (
            <Button onClick={handleConfirmScope} disabled={busy}>
              {isConfirming ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Done'
              )}
            </Button>
          ) : (
            <Button onClick={() => onOpenChange(false)} disabled={busy}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Which Supabase projects are in scope for scans.
 *
 * Each row is an `IntegrationConnection`; toggling `enabled` is the scoping
 * switch the check runner already honours, so nothing here needs new plumbing
 * on the scan side. Lives in Manage rather than in the connect popup because
 * scope changes over the life of an account — a project added next month
 * should be a resync, not a reconnect.
 */
function SupabaseProjectScopeList() {
  const projectsQuery = useQuery(
    trpc.integrations.listSupabaseProjects.queryOptions(),
  )
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)

  const projects = projectsQuery.data?.projects ?? []

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.listSupabaseProjects.queryKey(),
    })
  }

  const handleToggle = async (connectionId: string, enabled: boolean) => {
    setPendingId(connectionId)
    try {
      await trpcClient.integrations.setSupabaseProjectEnabled.mutate({
        connectionId,
        enabled,
      })
      await refresh()
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update project scope',
      )
    } finally {
      setPendingId(null)
    }
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await trpcClient.integrations.syncSupabaseProjects.mutate()
      await refresh()
      toast.success('Project list refreshed')
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to refresh project list',
      )
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <span className='text-[12px] font-medium uppercase tracking-wider text-text-subtle'>
          Projects in scope
        </span>
        <button
          type='button'
          onClick={handleSync}
          disabled={isSyncing}
          className='text-[12px] text-cobalt-strong hover:underline disabled:opacity-50'
        >
          {isSyncing ? 'Refreshing…' : 'Refresh list'}
        </button>
      </div>

      {projectsQuery.isLoading ? (
        <p className='text-[12px] text-text-subtle'>Loading projects…</p>
      ) : projects.length === 0 ? (
        <p className='text-[12px] text-text-subtle'>
          No projects found yet. Use “Refresh list” to enumerate them.
        </p>
      ) : (
        <div className='divide-y divide-border rounded-sm border border-border'>
          {projects.map((project) => (
            <label
              key={project.connectionId}
              className='flex cursor-pointer items-center justify-between gap-3 p-3'
            >
              <span className='min-w-0'>
                <span className='block truncate text-[13px] text-text-body'>
                  {project.name}
                </span>
                <span className='block truncate text-[11px] text-text-subtle'>
                  {project.projectRef}
                  {project.region ? ` · ${project.region}` : ''}
                  {/* A project Supabase stopped returning stays listed but
                      excluded, so a deleted project is visible rather than
                      silently vanishing from the audit scope. */}
                  {!project.present ? ' · no longer in Supabase' : ''}
                </span>
              </span>
              <input
                type='checkbox'
                checked={project.enabled}
                disabled={pendingId === project.connectionId}
                onChange={(e) =>
                  handleToggle(project.connectionId, e.target.checked)
                }
                className='h-4 w-4 shrink-0 accent-primary'
              />
            </label>
          ))}
        </div>
      )}
      <p className='text-[11px] text-text-subtle'>
        Unchecked projects are excluded from every scan. Individual checks can be
        narrowed further in their own settings.
      </p>
    </div>
  )
}

export const SupabaseProvider: IntegrationProvider = {
  ...supabaseApiKeyProvider,
  ConfigModal: (props) => {
    // Opt-in escape hatch: an OAuth-connected workspace can still choose to
    // fall back to a token (e.g. the OAuth app gets revoked org-side).
    const [useAccessToken, setUseAccessToken] = useState(false)

    if (isSupabaseOAuthConnection(props.integration) && !useAccessToken) {
      return (
        <SupabaseManageDialog
          {...props}
          onUseAccessToken={() => setUseAccessToken(true)}
          onOpenChange={(open) => {
            props.onOpenChange(open)
            if (!open) setUseAccessToken(false)
          }}
        />
      )
    }

    const ConfigModal = supabaseApiKeyProvider.ConfigModal
    return ConfigModal ? <ConfigModal {...props} /> : null
  },
  handleConnect: async ({ workspaceId, redirectUrl, onSuccess, onFinish }) => {
    // Ask the server first — an unconfigured OAuth app should degrade to the
    // token form, not to a popup that fails after the user has already
    // committed to a consent screen.
    let oauthAvailable = false
    try {
      const result = await trpcClient.integrations.supabaseOAuthAvailable.query()
      oauthAvailable = result.available
    } catch {
      oauthAvailable = false
    }

    if (!oauthAvailable) {
      // Same behaviour as every other api-key provider: open the config modal.
      onSuccess?.({ provider: 'Supabase' })
      onFinish?.('success')
      return { success: true }
    }

    return startSupabaseOAuth({ workspaceId, redirectUrl, onSuccess, onFinish })
  },
  onConnectComplete: async ({ code, state }) => {
    return trpcClient.integrations.completeSupabaseAuth.mutate({ code, state })
  },
}

export const WizProvider = createApiKeyProvider({
  id: 'Wiz',
  name: 'Wiz',
  description: 'Connect your Wiz cloud security account',
  authMode: 'oauth_client_credentials',
  docsUrl: 'https://docs.wiz.io/wiz-docs/docs/service-accounts',
  featureId: 'connection_settings',
  featureName: 'Connection settings',
  featureDescription: 'Manage Wiz service-account credentials',
  fields: [
    { id: 'clientId', label: 'Client ID', placeholder: 'Wiz service-account client ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'Wiz service-account client secret', type: 'password' },
    { id: 'apiUrl', label: 'API endpoint', placeholder: 'https://api.us17.app.wiz.io/graphql', helpText: 'Region-specific GraphQL endpoint shown in your Wiz tenant settings' },
    { id: 'authUrl', label: 'Auth endpoint', placeholder: 'https://auth.app.wiz.io', helpText: 'Defaults to https://auth.app.wiz.io. Override only for non-default tenants' },
  ],
})

export const OrcaProvider = createApiKeyProvider({
  id: 'Orca',
  name: 'Orca Security',
  description: 'Connect your Orca Security account',
  authMode: 'bearer_token',
  docsUrl: 'https://docs.orcasecurity.io/docs/api-authentication',
  featureId: 'connection_settings',
  featureName: 'Connection settings',
  featureDescription: 'Manage Orca API credentials',
  fields: [
    { id: 'apiKey', label: 'API token', placeholder: 'Orca API token', type: 'password', helpText: 'Issued from Orca Settings → API → Personal API Tokens' },
    { id: 'baseUrl', label: 'Base URL', placeholder: 'https://api.orcasecurity.io', helpText: 'US: https://api.orcasecurity.io · EU: https://api.eu.orcasecurity.io' },
  ],
})

// ---------------------------------------------------------------------------
// HR & People Ops (RFC-148)
// ---------------------------------------------------------------------------

export const BambooHRProvider = createApiKeyProvider({
  id: 'BambooHR',
  name: 'BambooHR',
  description: 'Employee lifecycle, onboarding/offboarding evidence for user provisioning controls',
  docsUrl: 'https://documentation.bamboohr.com/docs/getting-started',
  featureId: 'hr_evidence',
  featureName: 'HR evidence',
  featureDescription: 'Collect employee lifecycle and onboarding/offboarding evidence',
  fields: [
    { id: 'subdomain', label: 'BambooHR subdomain', placeholder: 'your-company', helpText: 'The subdomain from your-company.bamboohr.com' },
    { id: 'apiKey', label: 'API key', placeholder: 'BambooHR API key', type: 'password' },
  ],
})

export const RipplingProvider = createApiKeyProvider({
  id: 'Rippling',
  name: 'Rippling',
  description: 'Employee lifecycle, device management evidence for user provisioning and endpoint controls',
  authMode: 'bearer_token',
  featureId: 'hr_evidence',
  featureName: 'HR evidence',
  featureDescription: 'Collect employee lifecycle and device management evidence',
  fields: [
    { id: 'apiToken', label: 'API token', placeholder: 'Rippling API token', type: 'password', helpText: 'Create or request a token from your Rippling admin API access settings' },
  ],
})

export const DeelProvider = createApiKeyProvider({
  id: 'Deel',
  name: 'Deel',
  description: 'Employee and contractor lifecycle, onboarding/offboarding, contract compliance evidence',
  authMode: 'bearer_token',
  docsUrl: 'https://developer.deel.com/api/quickstart',
  featureId: 'hr_evidence',
  featureName: 'HR evidence',
  featureDescription: 'Collect employee/contractor lifecycle and compliance evidence',
  fields: [
    { id: 'apiToken', label: 'API token', placeholder: 'Deel API token', type: 'password', helpText: 'Create a token in Deel Developer Center > API Keys' },
  ],
})


export const WorkdayProvider = createApiKeyProvider({
  id: 'Workday',
  name: 'Workday',
  description: 'Employee lifecycle, onboarding/offboarding evidence for user provisioning controls',
  authMode: 'oauth_refresh_token',
  docsUrl: 'https://community.workday.com/sites/default/files/file-hosting/restapi/',
  featureId: 'hr_evidence',
  featureName: 'HR evidence',
  featureDescription: 'Collect employee lifecycle and onboarding/offboarding evidence',
  fields: [
    { id: 'restApiEndpoint', label: 'REST API endpoint', placeholder: 'https://wd2-impl-services1.workday.com/ccx/api/...' },
    { id: 'tokenUrl', label: 'Token URL', placeholder: 'https://wd2-impl-services1.workday.com/ccx/oauth2/.../token' },
    { id: 'clientId', label: 'Client ID', placeholder: 'API client ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'API client secret', type: 'password' },
    { id: 'refreshToken', label: 'Refresh token', placeholder: 'OAuth refresh token', type: 'password' },
  ],
})

// ---------------------------------------------------------------------------
// Finance & Billing (RFC-148)
// ---------------------------------------------------------------------------

export const StripeProvider = createApiKeyProvider({
  id: 'Stripe',
  name: 'Stripe',
  description: 'Transaction logs and PCI evidence for financial controls',
  docsUrl: 'https://docs.stripe.com/keys',
  featureId: 'finance_evidence',
  featureName: 'Finance evidence',
  featureDescription: 'Collect transaction logs and PCI evidence',
  fields: [
    { id: 'apiKey', label: 'Restricted API key', placeholder: 'rk_live_...', type: 'password', helpText: 'Use a restricted key with read-only permissions' },
  ],
})

export const BrexProvider = createApiKeyProvider({
  id: 'Brex',
  name: 'Brex',
  description: 'Expense policies and approval workflows for financial controls',
  authMode: 'bearer_token',
  docsUrl: 'https://developer.brex.com/docs/authentication/',
  featureId: 'finance_evidence',
  featureName: 'Finance evidence',
  featureDescription: 'Collect expense policies and approval workflows',
  fields: [
    { id: 'apiToken', label: 'API token', placeholder: 'Brex API token', type: 'password' },
  ],
})

// ---------------------------------------------------------------------------
// CRM & Sales (RFC-148)
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Collaboration (RFC-148)
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Cloud & Infrastructure (RFC-148)
// ---------------------------------------------------------------------------

export const M365Provider = createApiKeyProvider({
  id: 'M365',
  name: 'Microsoft 365',
  description: 'Scan Entra ID, Defender, Exchange, SharePoint, and Teams security configuration',
  authMode: 'oauth_client_credentials',
  docsUrl: 'https://learn.microsoft.com/en-us/graph/auth-register-app-v2',
  featureId: 'cloud_scan',
  featureName: 'Cloud scan',
  featureDescription: 'Run deterministic M365 compliance checks across Entra ID, Defender, Exchange, SharePoint, and Teams',
  fields: [
    { id: 'tenantId', label: 'Tenant ID', placeholder: 'Azure AD tenant ID' },
    { id: 'clientId', label: 'App registration client ID', placeholder: 'Application (client) ID' },
    { id: 'clientSecret', label: 'Client secret', placeholder: 'App registration client secret', type: 'password' },
  ],
})

export const KubernetesProvider = createApiKeyProvider({
  id: 'Kubernetes',
  name: 'Kubernetes',
  description: 'Scan API server, controller manager, etcd, kubelet, RBAC, and pod security configuration',
  authMode: 'bearer_token',
  featureId: 'cluster_scan',
  featureName: 'Cluster scan',
  featureDescription: 'Run deterministic Kubernetes security checks via cluster agent',
  fields: [
    { id: 'apiServer', label: 'API server URL', placeholder: 'https://your-cluster.example.com:6443' },
    { id: 'serviceAccountToken', label: 'Service account token', placeholder: 'Bearer token for cluster access', type: 'password', helpText: 'Create a service account with cluster-reader role binding' },
    { id: 'caCert', label: 'CA certificate (optional)', placeholder: 'Paste cluster CA certificate (PEM)', required: false, helpText: 'Required if the API server uses a self-signed certificate' },
  ],
})

// ---------------------------------------------------------------------------
// Incident response, endpoint & training
//
// Without an entry here (and in providers/index.ts) a tile's Connect button is
// inert: `openConnect` looks the IntegrationType up in the provider map and
// only `console.warn`s when it misses. The server-side adapter, checks and
// wiring can all be complete and the integration still be unreachable.
//
// Region/host is a first-class field on three of these, following Datadog's
// `site`: each vendor returns 401 — not 404 — when you call the wrong regional
// host, which is indistinguishable from a bad token. Asking beats guessing.
// ---------------------------------------------------------------------------

export const SentryProvider = createApiKeyProvider({
  id: 'Sentry',
  name: 'Sentry',
  description: 'Scan member two-factor enforcement, privileged role counts, and stale invitations',
  authMode: 'bearer_token',
  docsUrl: 'https://docs.sentry.io/api/guides/create-auth-token/',
  featureId: 'security_scan',
  featureName: 'Access review',
  featureDescription: 'Run deterministic Sentry access and 2FA checks',
  fields: [
    { id: 'token', label: 'Auth token', placeholder: 'Organization auth token', type: 'password', helpText: 'Settings > Developer Settings > Auth Tokens. Needs org:read and member:read.' },
    { id: 'orgSlug', label: 'Organization slug', placeholder: 'your-org', helpText: 'The slug in sentry.io/organizations/<slug>/' },
    { id: 'host', label: 'Host (self-hosted only)', placeholder: 'https://sentry.io', helpText: 'Leave blank for sentry.io. Set this only for a self-hosted install.' },
  ],
})

export const PagerDutyProvider = createApiKeyProvider({
  id: 'PagerDuty',
  name: 'PagerDuty',
  description: 'Scan escalation policies, on-call staffing, schedule coverage, and incident closure',
  docsUrl: 'https://support.pagerduty.com/main/docs/api-access-keys',
  featureId: 'security_scan',
  featureName: 'Incident response',
  featureDescription: 'Run deterministic PagerDuty incident-response checks',
  fields: [
    { id: 'apiKey', label: 'REST API key', placeholder: 'General Access REST API key', type: 'password', helpText: 'Use a GENERAL ACCESS (account-level) key. A personal key returns a silently filtered list rather than an error, so checks cannot tell "no escalation policies" from "not allowed to see them".' },
  ],
})

export const IncidentIoProvider = createApiKeyProvider({
  id: 'IncidentIo', name: 'incident.io',
  description: 'Collect account access, incidents, timelines, follow-ups and postmortems visible to this organization key. This includes private incident data when the key grants it.',
  docsUrl: 'https://docs.incident.io/admin/api-keys',
  featureId: 'security_scan', featureName: 'Access and incident evidence',
  featureDescription: 'Read-only evidence collection with explicit visibility limits',
  fields: [{ id: 'apiKey', label: 'Organization API key', placeholder: 'Organization API key', type: 'password',
    helpText: 'Create a dedicated organization key with View data and View catalog. Private incident access is optional; grant it only for the intended audit scope. Changes to the key’s account or permissions require reconnecting. Disconnecting Screenata does not revoke the key in incident.io.' }],
})

export const KandjiProvider = createApiKeyProvider({
  id: 'Kandji',
  name: 'Kandji',
  description: 'Scan Apple device encryption, firewall, hardening baseline, and MDM enrolment',
  authMode: 'bearer_token',
  docsUrl: 'https://api-docs.iru.com/',
  featureId: 'device_posture',
  featureName: 'Device posture',
  featureDescription: 'Run deterministic Kandji device-compliance checks',
  fields: [
    { id: 'apiToken', label: 'API token', placeholder: 'Kandji API token', type: 'password', helpText: 'Settings > Access > Add API Token. Enable the Devices and Prism permissions. They are off by default, even on existing tokens.' },
    { id: 'subdomain', label: 'Tenant subdomain', placeholder: 'your-company', helpText: 'The subdomain from your-company.api.kandji.io' },
    { id: 'region', label: 'Region', placeholder: 'us', helpText: 'us or eu. An EU tenant called on the US host returns 401, which looks identical to a bad token.' },
  ],
})

export const KnowBe4Provider = createApiKeyProvider({
  id: 'KnowBe4',
  name: 'KnowBe4',
  description: 'Scan security-awareness training completion across your whole employee roster',
  authMode: 'bearer_token',
  docsUrl: 'https://developer.knowbe4.com/reporting/',
  featureId: 'security_training',
  featureName: 'Security training',
  featureDescription: 'Run deterministic KnowBe4 training-coverage checks',
  fields: [
    { id: 'apiToken', label: 'Reporting API token', placeholder: 'KnowBe4 Reporting API token', type: 'password', helpText: 'Account Settings > Account Integrations > API. Requires Platinum, Diamond or SAT Foundations; lower tiers cannot issue a key.' },
    { id: 'region', label: 'Region', placeholder: 'us', helpText: 'us, eu, ca, uk or de. Must match the console URL you sign in to. The wrong region returns 401, which looks identical to a bad token.' },
  ],
})

export const VercelProvider = createApiKeyProvider({
  id: 'Vercel',
  name: 'Vercel',
  description: 'Scan team roles, SAML SSO enforcement, and stale team invitations',
  authMode: 'bearer_token',
  docsUrl: 'https://vercel.com/docs/rest-api#authentication',
  featureId: 'access_review',
  featureName: 'Access review',
  featureDescription: 'Run deterministic Vercel access and SSO checks',
  fields: [
    { id: 'apiToken', label: 'Access token', placeholder: 'Vercel access token', type: 'password', helpText: 'Account Settings > Tokens. Scope the token to the TEAM: a personal-account token cannot read team membership.' },
    { id: 'teamId', label: 'Team ID', placeholder: 'team_xxxxxxxxxxxx', helpText: 'Team Settings > General. Starts with team_.' },
  ],
})

export const RenderProvider = createApiKeyProvider({
  id: 'Render',
  name: 'Render',
  description: 'Inventory the services deployed in your Render workspace',
  authMode: 'bearer_token',
  docsUrl: 'https://render.com/docs/api',
  featureId: 'asset_inventory',
  featureName: 'Asset inventory',
  featureDescription: 'Enumerate deployed Render services for the asset register',
  fields: [
    { id: 'apiKey', label: 'API key', placeholder: 'Render API key', type: 'password', helpText: 'Account Settings > API Keys. Read access is enough. This connector never writes.' },
  ],
})
